import { FC, useEffect, useState } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { getAllPayments } from '@/services/subscription.service';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';
import { Search, Filter, RefreshCw, CheckCircle, Mail } from 'lucide-react';
import { Table, Column } from '@/components/ui/Table';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { Pagination } from '@/components/ui';

import { Payment } from '@/types/payment';

const InternalManualPayments: FC = () => {
    const { addToast } = useToast();

    // Payments State
    const [payments, setPayments] = useState<Payment[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Pagination
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [total, setTotal] = useState(0);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(1); // Reset page on search
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchPayments = async () => {
        setLoading(true);
        try {
            const result = await getAllPayments({
                search: debouncedSearch || undefined,
                status: statusFilter || undefined,
                page,
                limit
            });

            if (Array.isArray(result)) {
                setPayments(result);
                setTotal(result.length);
            } else {
                setPayments(result.data || []);
                setTotal(result.total || 0);
            }
        } catch (err) {
            console.error('Failed to fetch payments:', err);
            addToast('Failed to load payments.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPayments();
    }, [debouncedSearch, statusFilter, page]);

    const handleConfirmPayment = async (subscriptionId: string) => {
        try {
            await api.post(`/subscriptions/confirm-manual-payment/${subscriptionId}`);
            addToast('Payment confirmed!', 'success');
            fetchPayments();
        } catch (err) {
            addToast(getErrorMessage(err) || 'Failed to confirm payment.', 'error');
        }
    };

    const handleResendInvoice = async (subscriptionId: string) => {
        try {
            await api.post(`/subscriptions/resend-invoice/${subscriptionId}`);
            addToast('Invoice resent!', 'success');
        } catch (err) {
            addToast(getErrorMessage(err) || 'Failed to resend invoice.', 'error');
        }
    };

    const paymentColumns: Column<Payment>[] = [
        {
            key: 'user',
            header: 'Customer',
            className: 'w-[280px]',
            render: (p) => (
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-slate-600">
                            {p.user?.name ? p.user.name.charAt(0).toUpperCase() : '?'}
                        </span>
                    </div>
                    <div className="flex flex-col min-w-0">
                        <span className="text-sm font-semibold text-slate-900 truncate">{p.user?.name || 'Unknown User'}</span>
                        <span className="text-xs text-slate-500 truncate font-mono">{p.user?.email || 'No email'}</span>
                    </div>
                </div>
            )
        },
        {
            key: 'status',
            header: 'Status',
            className: 'w-[140px]',
            render: (p) => {
                const statusStyles = {
                    paid: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
                    pending: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
                    failed: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
                    refunded: { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', dot: 'bg-slate-500' },
                };
                const style = statusStyles[p.status as keyof typeof statusStyles] || statusStyles.refunded;

                return (
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${style.bg} ${style.text} ${style.border}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                        <span className="capitalize">{p.status}</span>
                    </div>
                );
            }
        },
        {
            key: 'amount',
            header: 'Amount',
            render: (p) => (
                <div className="flex flex-col">
                    <span className="text-sm font-bold text-slate-900 font-mono tracking-tight">
                        {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: p.currency || 'USD',
                        }).format(Number(p.amount || 0))}
                    </span>
                    <span className="text-[10px] text-slate-500 uppercase font-medium tracking-wider">
                        {p.currency || 'USD'}
                    </span>
                </div>
            )
        },
        {
            key: 'plan',
            header: 'Plan',
            render: (p) => {
                let name = p.plan?.displayName || p.plan?.name || 'Manual Plan';
                if (name.includes('Marketing & Sales Experience Manager')) name = name.replace('Marketing & Sales Experience Manager', 'MXM & SXM');
                else if (name.includes('Marketing Experience Manager')) name = name.replace('Marketing Experience Manager', 'MXM');
                else if (name.includes('Sales Experience Manager')) name = name.replace('Sales Experience Manager', 'SXM');

                return (
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-slate-50 text-xs font-medium text-slate-600 border border-slate-200 whitespace-nowrap">
                        {name}
                    </span>
                );
            }
        },
        {
            key: 'date',
            header: 'Date',
            render: (p) => {
                const date = new Date(p.createdAt);
                if (isNaN(date.getTime())) return <span className="text-slate-400 text-xs">—</span>;

                return (
                    <div className="flex flex-col whitespace-nowrap">
                        <span className="text-xs font-semibold text-slate-700">
                            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono mt-0.5">
                            {date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                );
            }
        },
        {
            key: 'actions',
            header: '',
            className: 'text-right min-w-[100px]',
            render: (p) => {
                const isManualPending = p.stripeSubscriptionId?.startsWith('sub_manual_') && p.status === 'pending';
                if (!isManualPending) return null;

                return (
                    <div className="flex items-center justify-end gap-1">
                        <button
                            onClick={(e) => { e.stopPropagation(); handleConfirmPayment(p.stripeSubscriptionId!); }}
                            className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors border border-transparent hover:border-emerald-200"
                            title="Confirm Payment"
                        >
                            <CheckCircle className="w-4 h-4" />
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); handleResendInvoice(p.stripeSubscriptionId!); }}
                            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-md transition-colors border border-transparent hover:border-slate-200"
                            title="Resend Invoice"
                        >
                            <Mail className="w-4 h-4" />
                        </button>
                    </div>
                );
            }
        }
    ];

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="space-y-6 animate-fadeIn">
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="w-full md:max-w-md relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <Input
                        placeholder="Search customers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 h-10 w-full bg-white border-slate-200 focus:border-slate-400 focus:ring-slate-400/10 transition-all font-medium text-slate-700 placeholder:text-slate-400"
                    />
                </div>

                <div className="flex items-center gap-3 w-full md:w-auto">
                    <div className="relative group w-full md:w-auto">
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none z-10" />
                        <select
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                            className="h-10 pl-9 pr-10 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-lg focus:ring-2 focus:ring-slate-900/5 focus:border-slate-300 block w-full md:w-48 outline-none appearance-none cursor-pointer hover:border-slate-300 transition-all"
                        >
                            <option value="">All Statuses</option>
                            <option value="paid">Paid</option>
                            <option value="pending">Pending</option>
                            <option value="failed">Failed</option>
                            <option value="refunded">Refunded</option>
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>

                    <Button variant="secondary" onClick={fetchPayments} className="h-10 w-10 p-0 flex items-center justify-center bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300 text-slate-600" disabled={loading} title="Refresh">
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white border border-slate-200 rounded-xl shadow-sm">
                    <Spinner size="lg" />
                    <p className="mt-4 text-sm text-slate-500 font-medium tracking-wide">Syncing ledger...</p>
                </div>
            ) : (
                <div className="space-y-4">
                    <Table
                        columns={paymentColumns}
                        data={payments}
                        emptyMessage="No transaction logs found matching your criteria."
                        hoverable={true}
                        variant="bordered"
                        className="bg-white rounded-xl shadow-sm overflow-hidden"
                    />

                    {/* Pagination Controls */}
                    {total > 0 && (
                        <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4 px-2 mt-4">
                            <span className="text-sm text-slate-500 font-medium">
                                Showing {Math.min((page - 1) * limit + 1, total)} to {Math.min(page * limit, total)} of {total} results
                            </span>
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                onPageChange={setPage}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export { InternalManualPayments };
